import {
  DatabaseIcon,
  FileTextIcon,
  FolderOpen,
  HomeIcon,
  LayoutDashboard,
  LayoutTemplateIcon,
  MessageCircleIcon,
  PanelsTopLeftIcon,
  PlayCircleIcon,
  ScanSearch,
  Settings,
  Users2Icon,
  Scale,
  Table2Icon,
} from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Organization } from "./api/organizations";
import { AIIcon } from "./components/ui/icons";

export interface SidebarSubitemsT {
  title: string;
  path: string;
  selected?: (searchParams: URLSearchParams) => boolean;
  canAccess?: (org: Organization) => boolean;
  /** Sidebar badge key — sidebar resolves this to a count value */
  badgeKey?: string;
}

export const ROUTES = [
  {
    name: "Link",
    path: "/l/:linkToken",
    component: React.lazy(() => import("./views/link")),
    hidden: true,
  },
  {
    name: "Home",
    path: "/",
    component: React.lazy(() => import("./views/home")),
    description: () => ({
      context:
        "Home page — user's landing page with recent activity, dashboards, and suggestions. Help the user discover what they can do, find their projects and dashboards, and navigate the platform.",
      agent: "home",
    }),
    icon: HomeIcon,
    hidden: false,
  },
  {
    name: "Portfolios",
    path: "/portfolios/",
    match: "/portfolios/*",
    component: React.lazy(() => import("./views/portfolios")),
    description: () => ({
      context: "List of all the organization's portfolios",
      agent: "portfolio",
    }),
    icon: FileTextIcon,
    sidebarSubitems: [
      {
        title: "All",
        path: "/portfolios?page=1",
        selected: (searchParams: URLSearchParams) =>
          searchParams.get("filter") === "" ||
          searchParams.get("filter") === null,
        // canAccess: (org: Organization) => {
        //   return org?.organization_class === "seller";
        // },
      },
      {
        title: "Preliminary",
        path: "/portfolios?filter=preliminary&page=1",
        selected: (searchParams: URLSearchParams) =>
          searchParams.get("filter") === "preliminary",
        // canAccess: (org: Organization) => {
        //   return org?.organization_class === "seller";
        // },
      },
      {
        title: "Listed",
        path: "/portfolios?filter=listed&page=1",
        selected: (searchParams: URLSearchParams) =>
          searchParams.get("filter") === "listed",
        // canAccess: (org: Organization) => {
        //   return org?.organization_class === "seller";
        // },
      },
      {
        title: "Closed",
        path: "/portfolios?filter=closed&page=1",
        selected: (searchParams: URLSearchParams) =>
          searchParams.get("filter") === "closed",
        // canAccess: (org: Organization) => {
        //   return org?.organization_class === "seller";
        // },
      },
    ],
    children: [
      {
        path: "/create",
        component: React.lazy(() => import("./views/portfolios-create")),
        description: () => ({
          context: "Create a new portfolio",
          agent: "portfolio",
        }),
      },
      {
        path: "/archived/",
        component: React.lazy(() => import("./views/portfolios")),
        description: () => ({
          context: "List of all the organization's archived portfolios",
          agent: "portfolio",
        }),
      },
      {
        path: "/archived/:portfolioId/",
        component: React.lazy(() => import("./views/portfolios")),
        description: (params: { portfolioId?: string }) => ({
          context: `View of an archived portfolio {portfolio_name} (id: {portfolio_id})`,
          agent: "portfolio",
          missing_vars: ["portfolio_name"],
          known_vars: {
            portfolio_id: params.portfolioId || "",
          },
        }),
      },
      {
        path: "/archived/:portfolioId/:portfolioTab",
        component: React.lazy(() => import("./views/portfolios")),
        description: (params: {
          portfolioId?: string;
          portfolioTab?: string;
        }) => ({
          context: `View of an archived portfolio {portfolio_name} (id: {portfolio_id}) with specific tab {portfolio_tab}`,
          agent: "portfolio",
          missing_vars: ["portfolio_name"],
          known_vars: {
            portfolio_id: params.portfolioId || "",
            portfolio_tab: params.portfolioTab || "",
          },
        }),
      },
      {
        path: "/:portfolioId",
        component: () => {
          let { portfolioId } = useParams();
          let navigate = useNavigate();

          React.useEffect(() => {
            navigate(`/portfolios/${portfolioId}/projects`);
          }, [portfolioId, navigate]);

          return null;
        },
      },
      {
        path: "/:portfolioId/:portfolioTab",
        component: React.lazy(() => import("./views/portfolios")),
        description: (params: {
          portfolioId?: string;
          portfolioTab?: string;
        }) => ({
          context: `View of a portfolio {portfolio_name} (id: {portfolio_id}) with specific tab {portfolio_tab}`,
          agent: "portfolio",
          missing_vars: ["portfolio_name"],
          known_vars: {
            portfolio_id: params.portfolioId || "",
            portfolio_tab: params.portfolioTab || "",
          },
        }),
      },
      {
        path: "/:portfolioId/:portfolioTab/:portfolioSubTab",
        component: React.lazy(() => import("./views/portfolios")),
        description: (params: {
          portfolioId?: string;
          portfolioTab?: string;
          portfolioSubTab?: string;
        }) => ({
          context: `View of a portfolio {portfolio_name} (id: {portfolio_id}) with specific tab {portfolio_tab} and subtab {portfolio_subtab}`,
          agent: "portfolio",
          missing_vars: ["portfolio_name"],
          known_vars: {
            portfolio_id: params.portfolioId || "",
            portfolio_tab: params.portfolioTab || "",
            portfolio_subtab: params.portfolioSubTab || "",
          },
        }),
      },
    ],
    hidden: false,
    // canAccess: (org: Organization) => {
    //   return (
    //     org?.permissions?.includes("portfolios") ||
    //     org?.permissions?.includes("some_portfolios")
    //   );
    // },
  },
  {
    name: "Projects",
    path: "/projects/",
    match: "/projects/*",
    component: React.lazy(() => import("./views/projects")),
    description: () => ({
      context: "List of all the organization's projects",
      agent: "project",
    }),
    icon: PanelsTopLeftIcon,
    children: [
      {
        path: "/archived/",
        component: React.lazy(() => import("./views/projects")),
        description: () => ({
          context: "List of all the organization's archived projects",
          agent: "project",
        }),
      },
      {
        path: "/archived/:projectId/",
        component: React.lazy(() => import("./views/projects")),
        description: (params: { projectId?: string }) => ({
          context: `View of an archived project {project_name} (id: {project_id})`,
          agent: "project",
          missing_vars: ["project_name"],
          known_vars: {
            project_id: params.projectId || "",
          },
        }),
      },
      {
        path: "/archived/:projectId/:projectTab",
        component: React.lazy(() => import("./views/projects")),
        description: (params: { projectId?: string; projectTab?: string }) => ({
          context: `View of an archived project {project_name} (id: {project_id}) with specific tab {project_tab}`,
          agent: "project",
          missing_vars: ["project_name"],
          known_vars: {
            project_id: params.projectId || "",
            project_tab: params.projectTab || "",
          },
        }),
      },
      {
        path: "/:projectId/:projectTab",
        component: React.lazy(() => import("./views/projects")),
        description: (params: { projectId?: string; projectTab?: string }) => ({
          context: `View of a project {project_name} (id: {project_id}) with specific tab {project_tab}`,
          agent: "project",
          missing_vars: ["project_name"],
          known_vars: {
            project_id: params.projectId || "",
            project_tab: params.projectTab || "",
          },
        }),
      },
    ],
    // canAccess: (org: Organization) => {
    //   return (
    //     org?.organization_class === "seller" &&
    //     (org?.permissions?.includes("projects") ||
    //       org?.permissions?.includes("some_projects"))
    //   );
    // },
  },
  {
    name: "Document Data",
    path: "/key-data/",
    match: "/key-data/*",
    component: React.lazy(() => import("./views/doc-key-data")),
    description: () => ({
      context:
        "Document Data Explorer — cross-project view of extracted document metadata including doc types, counterparties, effective dates, and document relationships",
      agent: "home",
    }),
    icon: DatabaseIcon,
    canAccess: (org: Organization) => {
      if (!org) return false;
      return org?.ai_ingestion_enabled;
    },
  },
  {
    name: "Variables Explorer",
    path: "/variables/",
    match: "/variables/*",
    component: React.lazy(() => import("./views/variables-explorer")),
    description: () => ({
      context:
        "Variables Explorer — cross-project matrix view of extracted variable values like MW, MWh, technology, outage data across projects",
      agent: "variables_explorer",
    }),
    icon: Table2Icon,
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Dashboards",
    path: "/dashboards/",
    match: "/dashboards/*",
    matchPaths: ["/dashboards/*", "/dashboard-templates/*"], // Match both for sidebar selection
    component: React.lazy(() => import("./views/dashboards")),
    description: () => ({
      context: "List of dashboards available to the organization",
      agent: "dashboard",
    }),
    icon: LayoutDashboard,
    hidden: false,
    sidebarSubitems: [
      {
        title: "Active Dashboards",
        path: "/dashboards",
        canAccess: () => true,
      },
      {
        title: "Templates",
        path: "/dashboard-templates",
        canAccess: () => true,
      },
    ],
    children: [
      {
        path: "/:dashboardId",
        component: React.lazy(() => import("./views/dashboards")),
        description: (params: { dashboardId?: string }) => {
          return {
            context: `Dashboard view for dashboard "{dashboard_name}" (id: {dashboard_id})`,
            agent: "dashboard",
            missing_vars: ["dashboard_name"],
            known_vars: {
              dashboard_id: params.dashboardId || "",
            },
          };
        },
      },
    ],
  },
  {
    name: "Dashboard Templates",
    path: "/dashboard-templates/",
    match: "/dashboard-templates/*",
    component: React.lazy(() => import("./views/dashboard-templates")),
    description: () => ({
      context: "Manage dashboard templates",
      agent: "dashboard",
    }),
    icon: LayoutTemplateIcon,
    hidden: true,
    children: [
      {
        path: "/:templateId",
        component: React.lazy(() => import("./views/dashboard-templates")),
        description: (params: { templateId?: string }) => {
          return {
            context: `Template view for template (id: {template_id})`,
            agent: "dashboard",
            known_vars: {
              template_id: params.templateId || "",
            },
          };
        },
      },
    ],
  },
  {
    name: "AI Setup",
    path: "/builder/",
    component: React.lazy(() => import("./views/builder")),
    icon: AIIcon,
    sidebarSubitems: [
      { title: "Analysis Builder", path: "/builder", canAccess: () => true },
      { title: "Question Bank", path: "/question-bank", canAccess: () => true },
      { title: "Rule Sets", path: "/rule-sets", canAccess: () => true },
      {
        title: "Document Types",
        path: "/document-types",
        canAccess: () => true,
        badgeKey: "docTypeSuggestions",
      },
      { title: "Org Context", path: "/org-context", canAccess: () => true },
      { title: "Personas", path: "/deep-dive-personas", canAccess: () => true },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Question Bank",
    path: "/question-bank/",
    match: "/question-bank/*",
    component: React.lazy(() => import("./views/questions")),
    description: () => ({
      context: "List of all existing Questions",
      agent: "questions",
    }),
    icon: AIIcon,
    hidden: true,
    children: [
      {
        path: "/:questionId/:questionTab",
        component: React.lazy(() => import("./views/questions")),
      },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Analysis Builder",
    path: "/builder/",
    match: "/builder/*",
    component: React.lazy(() => import("./views/builder")),
    description: () => ({
      context: "List of all existing Analyses",
      agent: "analysis",
    }),
    icon: ScanSearch,
    hidden: true,
    children: [
      {
        path: "/:analysisId/setup",
        component: React.lazy(() => import("./views/builder")),
        description: (params: { analysisId?: string }) => ({
          context:
            "Analysis setup view for {analysis_name} (id: {analysis_id})",
          agent: "analysis",
          missing_vars: ["analysis_name"],
          known_vars: {
            analysis_id: params.analysisId || "",
            analysis_tab: "setup",
          },
        }),
      },
      {
        path: "/:analysisId/reports",
        component: React.lazy(() => import("./views/builder")),
        description: (params: { analysisId?: string }) => ({
          context:
            "Analysis reports overview for {analysis_name} (id: {analysis_id})",
          agent: "analysis",
          missing_vars: ["analysis_name"],
          known_vars: {
            analysis_id: params.analysisId || "",
            analysis_tab: "reports",
          },
        }),
      },
      {
        path: "/:analysisId/reports/:reportId",
        component: React.lazy(() => import("./views/builder")),
        description: (params: { analysisId?: string; reportId?: string }) => ({
          context:
            "Analysis report {report_name} ({report_status}) (id: {report_id}) for {analysis_name} (id: {analysis_id}). Scope {analysis_scope}, items: {analysis_items}",
          agent: "analysis",
          missing_vars: [
            "analysis_name",
            "report_name",
            "analysis_scope",
            "analysis_items",
            "report_status",
          ],
          known_vars: {
            analysis_id: params.analysisId || "",
            report_id: params.reportId || "",
            analysis_tab: "reports",
          },
        }),
      },
      {
        path: "/:analysisId/*",
        component: React.lazy(() => import("./views/builder")), // Todo - change this to "analysis ID",
        description: (params: { analysisId?: string }) => ({
          context:
            "Analysis builder for {analysis_name} (id: {analysis_id}). Scope: {analysis_scope}, items: {analysis_items}",
          agent: "analysis",
          missing_vars: ["analysis_name", "analysis_scope", "analysis_items"],
          known_vars: {
            analysis_id: params.analysisId || "",
          },
        }),
      },
      {
        path: "/new",
        component: React.lazy(() => import("./views/builder")),
        description: () => ({
          context: "Creating a new analysis in the builder",
          agent: "analysis",
        }),
      },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Rule Sets",
    path: "/rule-sets/",
    match: "/rule-sets/*",
    component: React.lazy(() => import("./views/rule-sets")),
    description: () => ({
      context: "List of all existing Rule Sets",
      agent: "rules",
    }),
    icon: Scale,
    hidden: true,
    children: [
      {
        path: "/:ruleSetId/*",
        component: React.lazy(() => import("./views/rule-sets")),
        description: (params: { ruleSetId?: string }) => ({
          context: "Rule Set detail view for {ruleset_name} (id: {ruleset_id})",
          agent: "rules",
          missing_vars: ["ruleset_name"],
          known_vars: {
            ruleset_id: params.ruleSetId || "",
          },
        }),
      },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Workflows",
    path: "/workflows/",
    match: "/workflows/*",
    component: React.lazy(() => import("./views/workflows")),
    description: () => ({
      context: "List of all automation workflows",
      agent: "workflows",
    }),
    icon: PlayCircleIcon,
    children: [
      {
        path: "/new",
        component: React.lazy(() => import("./views/workflow-builder")),
        description: () => ({
          context: "Creating a new workflow in the workflow builder",
          agent: "workflows",
        }),
      },
      {
        path: "/:workflowId",
        component: React.lazy(() => import("./views/workflow-builder")),
        description: (params: { workflowId?: string }) => ({
          context: `Editing workflow (id: ${params.workflowId})`,
          agent: "workflows",
          known_vars: {
            workflow_id: params.workflowId || "",
          },
        }),
      },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Org Context",
    path: "/org-context/",
    match: "/org-context/*",
    component: React.lazy(() => import("./views/org-context")),
    description: () => ({
      context:
        "List of all Organization Context collections for context-aware analysis",
      agent: "analysis",
    }),
    icon: FolderOpen,
    hidden: true,
    children: [
      {
        path: "/:contextId",
        component: React.lazy(() => import("./views/org-context")),
        description: (params: { contextId?: string }) => ({
          context: "Organization Context detail view (id: {context_id})",
          agent: "analysis",
          known_vars: {
            context_id: params.contextId || "",
          },
        }),
      },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Document Types",
    path: "/document-types/",
    match: "/document-types/*",
    component: React.lazy(() => import("./views/document-templates")),
    description: () => ({
      context:
        "The user is on the Document Types page where they manage the organization's " +
        "document type taxonomy. These types are used for document classification, " +
        "analysis report recommendations, and RFI generation.",
      agent: "organization",
    }),
    icon: FileTextIcon,
    hidden: true,
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Deep Dive Personas",
    path: "/deep-dive-personas/",
    match: "/deep-dive-personas/*",
    component: React.lazy(() => import("./views/deep-dive-personas")),
    description: () => ({
      context:
        "Deep Dive Personas management — create and edit reusable audience/tone templates that prime deep dive generation for specific readers (e.g., tax equity investor, lender due diligence, investment committee memo)",
      agent: "analysis",
    }),
    icon: AIIcon,
    hidden: true,
    canAccess: (org: Organization) => {
      if (!org) return false;
      return (
        org.ai_ingestion_enabled &&
        (org.user_meta.is_admin || org.user_meta.questions_access !== "none")
      );
    },
  },
  {
    name: "Chat with documents",
    sidebarLink: "/chats",
    path: "/chats",
    match: "/chats/*",
    component: React.lazy(() => import("./views/chats")),
    icon: MessageCircleIcon,
    children: [
      {
        path: "/:itemId",
        component: React.lazy(() => import("./views/chats")),
      },
    ],
    canAccess: (org: Organization) => {
      if (!org) return false;
      return org?.ai_ingestion_enabled;
    },
  },
  // {
  //   name: "Task management",
  //   path: "/task-management",
  //   component: React.lazy(() => import("./views/global-task-manager")),
  //   icon: ListTodoIcon,
  // },
  {
    name: "Team management",
    sidebarLink: "/team-management/members",
    path: "/team-management/:teamTab",
    match: "/team-management/*",
    component: React.lazy(() => import("./views/team-management")),
    icon: Users2Icon,
    canAccess: (org: Organization) => {
      if (!org) return false;
      return org?.is_admin;
    },
    hidden: true,
  },
  // // {
  // //   name: "Activity log",
  // //   path: "/activity",
  // //   component: () => "Activity",
  // //   icon: Train,
  // // },
  // // {
  // //   name: "Users",
  // //   path: "/users",
  // //   component: () => "Users",
  // //   icon: Users2Icon,
  // // },
  // // {
  // //   name: "Reporting",
  // //   path: "/reporting",
  // //   component: () => "Reporting",
  // //   icon: ClipboardCheck,
  // // },

  // // {
  // //   name: "Preferences",
  // //   path: "/preferences",
  // //   component: () => "Preferences",
  // //   icon: Settings2,
  // // },
  // // {
  // //   name: "Account Settings",
  // //   path: "/account-settings",
  // //   component: () => "Account Settings",
  // //   icon: Settings,
  // // },
  {
    name: "Organization Settings",
    path: "/organization/:organizationTab",
    match: "/organization/*",
    component: React.lazy(() => import("./views/organization")),
    icon: Settings,
    hidden: true,
    canAccess: (org: Organization) => {
      if (!org) return false;
      return org?.is_admin;
    },
    children: [
      {
        path: "/details",
        component: React.lazy(() => import("./views/organization")),
      },
      {
        path: "/project-fields",
        component: React.lazy(() => import("./views/organization")),
      },
      {
        path: "/project-folder-categories",
        component: React.lazy(() => import("./views/organization")),
        description: () => ({
          context: "Organization settings → Project Folder configuration",
          agent: "organization",
        }),
      },
      {
        path: "/ai-usage",
        component: React.lazy(() => import("./views/organization")),
      },
      {
        path: "/integrations",
        component: React.lazy(() => import("./views/organization")),
      },
    ],
  },
  {
    name: "Account",
    path: "/account/:accountTab",
    component: React.lazy(() => import("./views/account")),
    icon: Settings,
    hidden: true,
  },
];
